import React, { useCallback, useRef, useState } from 'react';
import { MCButton, MCError } from '../../basic';
import { api, lang, remCss } from 'src/helpers';
import LANG_CONST from 'src/helpers/lang/const';
import { TimeAndLocation } from './TimeAndLocation';
import { WorkingWeeks } from './WorkingWeeks';
import { WorkingDays } from './WorkingDays';
import {
  generateData,
  generateUnsavedSlots,
  generateWorkingDays,
  generateWorkingWeeks,
  validDays,
  validWeeks
} from '../utils';
import mainModel, { models, STEPS } from '../model';
import moment from 'moment';
import errorDescriptions from 'src/constants/errors';

const model = models[STEPS.GENERATE_SCHEDULE];

export const GenerateSchedule = ({ onHide, onPrev, onDone }) => {
  model.use();
  const { doctorId } = mainModel.state;
  const hospitalParams = mainModel.getHospitalParams();

  const [step, setStep] = useState('local');
  const [duration, setDuration] = useState(20);
  const [hospital, setHospital] = useState(null);
  const [days, setDays] = useState([]);
  const [invalid, setInvalid] = useState({});
  const [loading, setLoading] = useState(false);
  const [weeks, setWeeks] = useState([]);
  const dataRef = useRef({ backData: {}, history: [] });

  const previousFn = useCallback(() => {
    switch (step) {
      case 'local':
        if (onPrev) onPrev();
        break;
      default:
        dataRef.current.backData = { days, hospital, duration };
        setInvalid({});
        setStep(dataRef.current.history.pop());
        break;
    }
  }, [days, hospital, duration, step, onPrev]);

  const nextFn = useCallback(() => {
    const backData = dataRef.current.backData;

    let invalid;
    switch (step) {
      case 'local': {
        const hasChange =
          hospital?.hospitalId !== backData?.hospital?.hospitalId ||
          duration !== backData?.duration;
        dataRef.current.history.push(step);

        const { startDate, endDate } = model.state;

        const gen = generateWorkingDays(hospital, duration);
        if (hasChange) setDays(gen);

        if (startDate && moment(endDate).diff(startDate, 'days') <= 7) {
          if (hasChange) setWeeks(generateWorkingWeeks(gen, startDate, endDate));
          setStep('weeks');
        } else {
          setStep('days');
        }
        break;
      }
      case 'days': {
        invalid = validDays(days, hospital);

        if (invalid.length > 0) {
          setInvalid(invalid);
        } else {
          dataRef.current.history.push(step);

          const hasChange = JSON.stringify(days) !== JSON.stringify(backData?.days);

          if (!hasChange) return setStep('weeks');

          const { startDate, endDate } = model.state;
          setWeeks(generateWorkingWeeks(days, startDate, endDate));
          setStep('weeks');
        }
        break;
      }
      case 'weeks': {
        invalid = validWeeks(weeks, hospital);

        if (invalid.length > 0) {
          setInvalid(invalid);
        } else {
          dataRef.current.history.push(step);

          setLoading(true);
          const params = {
            startDate: weeks[0].startOfWeek,
            endDate: weeks[weeks.length - 1].endOfWeek,
            days: generateData(weeks),
            ...hospital,
            duration,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            id: doctorId
          };
          api('schedule/generate', params).then(({ data, error }) => {
            if (!error) {
              if (data.bookedList) {
                mainModel.setNextStep(STEPS.BOOKED_LIST, {
                  list: data.bookedList,
                  params,
                  unsavedSlots: generateUnsavedSlots(weeks, hospital),
                  onDone: (resolvedList, cb) => {
                    api('schedule/generate', { ...params, resolvedList }).then(
                      ({ data, error }) => {
                        cb(error, data);
                        if (!error) {
                          mainModel.backStep();
                          if (onDone) onDone();
                          else onHide();
                        }
                      }
                    );
                  }
                });
              } else {
                if (onDone) onDone();
                else onHide();
              }
            } else {
              setInvalid({ error: errorDescriptions(error) });
            }
            setLoading(false);
          });
        }
        break;
      }
    }
    dataRef.current.backData = {};
  }, [hospital, duration, weeks, days, step]);

  const steps = {
    local: {
      title: LANG_CONST.text.choose_duration,
      component: (
        <TimeAndLocation
          duration={duration}
          setDuration={setDuration}
          hospitalParams={hospitalParams}
          hospital={hospital}
          setHospital={setHospital}
        />
      )
    },
    days: {
      title: LANG_CONST.text.choose_working_day,
      component: (
        <WorkingDays
          days={days}
          setDays={setDays}
          hospital={hospital}
          duration={duration}
          invalid={invalid}
        />
      )
    },
    weeks: {
      title:
        weeks.length > 0 &&
        (weeks.length > 1
          ? LANG_CONST.text.choose_working_week
          : weeks[0].days?.length > 1
            ? LANG_CONST.text.choose_working_day
            : LANG_CONST.text.setup_time_slot),
      component: (
        <WorkingWeeks
          weeks={weeks}
          setWeeks={setWeeks}
          invalid={invalid}
          days={days}
          duration={duration}
          hospital={hospital}
        />
      )
    }
  };

  const activeStep = steps[step];

  return (
    <>
      <div className='mc-modal-header'>
        <div className='mc-popup-title'>{lang.t(activeStep.title)}</div>
      </div>
      {invalid.error && (
        <div style={{ paddingBottom: 0 }}>
          <MCError error={invalid.error} />
        </div>
      )}
      {activeStep.component}
      <div className='mc-modal-footer' style={{ gap: remCss(20) }}>
        {step === 'local' && onPrev && (
          <MCButton
            medium
            light
            radius
            primary
            disabled={loading}
            style={{ width: remCss(100) }}
            label={LANG_CONST.buttons.back}
            onClick={onPrev}
          />
        )}
        {step !== 'local' && (
          <MCButton
            medium
            light
            radius
            primary
            disabled={loading}
            style={{ width: remCss(100) }}
            label={LANG_CONST.buttons.back}
            onClick={previousFn}
          />
        )}
        <div className={'mc-grow'} />
        <MCButton
          medium
          light
          radius
          primary
          label={LANG_CONST.buttons.cancel}
          onClick={onHide}
          style={{ width: remCss(100) }}
        />
        <MCButton
          medium
          dark
          radius
          primary
          disabled={loading}
          loading={loading}
          style={{ width: remCss(100) }}
          label={step === 'week' ? LANG_CONST.buttons.save : LANG_CONST.buttons.next}
          onClick={nextFn}
        />
      </div>
    </>
  );
};
